<template>
  <div v-if="allSections.length">
    <!-- options -->
    <BaseButton
      :text="`Back to 'Your application'`"
      flat
      ripple
      class="font-weight-semi-bold d-flex back-btn back-previous__btn-link pa-0 mb-sm-5"
      icon="mdi-chevron-left"
      color="psdarkgreen"
      small
      @click="$router.push({ name: ROUTE_NAME.HOME.B2B.APPLICATION })"
    />
    <div>
      <div class="d-flex justify-space-between align-center mt-6">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Payment
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="psdarkgreen"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <!-- the form -->
    <ValidationObserver ref="observerFormPaymentDetails" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <BaseLabel text="Payment method" class="mt-6 mt-md-10 mb-2" />
        <BaseRadio
          v-model="questionPaymentMethod.Value"
          pslStyle="button"
          class="pt-3 payment-method-radio"
          data-uiid="PaymentMethod"
          :hideDetails="false"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          width="max-content"
          :items="questionPaymentMethodItems"
          :rules="{
            required: {
              fieldLabel: 'Payment method',
            },
          }"
          aria-label="Payment method"
        />

        <!-- payment frequency -->
        <template v-if="isDirectDebitPayment">
          <BaseLabel text="Payment frequency" class="mb-2" />
          <BaseSelect
            v-model="questionPaymentFrequency.Value"
            dataUiid="PaymentFrequency"
            :items="questionPaymentFrequencyItems"
            :rules="{
              required: {
                fieldLabel: 'Payment frequency',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Payment frequency"
          />
        </template>

        <LayoutApplicationSummaryCard
          class="mt-12 mb-13"
          @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
          @showPremiumBreakdown3Months="showPremiumBreakdown3Months"
          @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
          @shareQuote="shareQuote"
          @printQuote="printQuote"
          :productGroup="productGroup"
          :showPayByCreditDebitCardMessage="false"
          :useRetrievedPaymentInfo="$route.params.type === 'resumeQuote'"
        />

        <!-- direct debit content -->
        <template v-if="isDirectDebitPayment">
          <template v-if="isAdvisedSeller">
            <BaseAlert color="var(--blue-100)" class="pa-4">
              <div class="d-flex flex-no-wrap">
                <div class="mr-3">
                  <v-icon color="var(--blue-500)">mdi-information</v-icon>
                </div>
                <div v-if="hasMonthlyPayment">
                  <p>
                    Please read: <br />
                    I will confirm the full details of the credit agreement in
                    writing; however, I will now explain the key points of the
                    instalment facility, which you should consider carefully.
                    The key points are: · The finance facility is provided by
                    Paymentshield Limited · [For
                    {{ premium.CreditDetails.TotalNumberOfPayments }}
                    monthly payments: the loan amount includes interest charges
                    at
                    {{
                      premium.CreditDetails.FixedInterestRate | formatPercent
                    }}, which represents an APR of
                    {{ premium.CreditDetails.Apr | formatPercent }}. The
                    premiums will be collected by 1 monthly instalment of
                    {{ premium.CreditDetails.InitialPayment | formatCurrency }}
                    and
                    {{ premium.CreditDetails.NumberOfSubsequentPayments }}
                    subsequent monthly instalments of
                    {{
                      premium.CreditDetails.SubsequentPayments | formatCurrency
                    }}]
                  </p>
                  <p
                    v-if="
                      questionPaymentFrequency.Value ===
                      MONTHLY_3_MONTHS_DEFERRED
                    "
                  >
                    Please note that this is a 12-month policy with the first
                    three months deferred. If this policy is cancelled early,
                    you will be charged for this policy on a pro rata daily
                    basis. If no payments are received on this policy, the
                    policy will be backdated to inception with no cover
                    honoured. On renewal, the policy will revert to a 12 monthly
                    payment plan.
                  </p>
                  <p>
                    By selecting the monthly payment option, you've chosen to
                    enter into a credit agreement. Failure to keep up the
                    payments is a breach of this agreement and may result in the
                    cancellation of your insurance policy. Please confirm that
                    the repayments are affordable, and you are happy to enter
                    into a credit agreement by answering yes. You have the right
                    to withdraw from the credit agreement if you tell us within
                    14 days from when the policy starts. If you do, you will
                    have to repay any credit and you will be responsible for
                    making alternative arrangements to pay for your cover as
                    your policy may be cancelled. If you feel the payments may
                    not be affordable, please consider other payment options. Do
                    you have any questions?
                  </p>
                  <p>
                    Direct Debits are protected by a guarantee, would you like
                    me to read this to you? <br />
                    Yes? - Please click:
                    <a
                      href="https://www.directdebit.co.uk/direct-debit-explained/direct-debit-guarantee/"
                      target="_blank"
                      >Direct Debit Guarantee</a
                    >
                    <br />
                    No? - This can be found on the direct debit website:
                    <a
                      href="https://www.directdebit.co.uk/direct-debit-explained/direct-debit-guarantee/"
                      target="_blank"
                      >Direct Debit Guarantee</a
                    >
                  </p>
                  <p class="mb-0">
                    It takes 10 working days to set up a new direct debit and
                    the date of the first payment will be confirmed in your
                    insurance documents.
                  </p>
                </div>
                <div v-else>
                  <p>
                    Direct Debits are protected by a guarantee, would you like
                    me to read this to you? <br />
                    Yes? - Please click:
                    <a
                      href="https://www.directdebit.co.uk/direct-debit-explained/direct-debit-guarantee/"
                      target="_blank"
                      >Direct Debit Guarantee</a
                    >
                    <br />
                    No? - This can be found on the direct debit website:
                    <a
                      href="https://www.directdebit.co.uk/direct-debit-explained/direct-debit-guarantee/"
                      target="_blank"
                      >Direct Debit Guarantee</a
                    >
                  </p>
                  <p class="mb-0">
                    It takes 10 working days to set up a new direct debit and
                    the date of the first payment will be confirmed in your
                    insurance documents.
                  </p>
                </div>
              </div>
            </BaseAlert>
          </template>

          <template v-else>
            <BaseAlert
              v-if="hasMonthlyPayment"
              color="var(--blue-100)"
              class="pa-4"
            >
              <div class="d-flex flex-no-wrap">
                <div class="mr-3">
                  <v-icon color="var(--blue-500)">mdi-information</v-icon>
                </div>
                <div>
                  <span class="font-weight-bold">Important!</span> By selecting
                  the monthly payment option you've chosen to enter into a
                  credit agreement. Failure to keep up with payments is a breach
                  of this agreement and may result in the cancellation of your
                  insurance policy. Please tick the box below to confirm that
                  the repayments are affordable and you're happy to enter into a
                  credit agreement. If you feel the payments may not be
                  affordable, please consider other payment options.
                </div>
              </div>
            </BaseAlert>
          </template>

          <BaseCheckBox
            :vid="questionRepaymentConfirmation.InterfaceKey"
            v-if="hasMonthlyPayment || isAdvisedSeller"
            className="mt-3 font-weight-bold primary--text"
            label="I confirm that I have read, understood and agreed to the above statement."
            hideDetails="auto"
            :checked="questionRepaymentConfirmation.Value"
            :rules="{
              required: {
                fieldLabel: 'Repayment confirmation',
              },
            }"
            @change="(val) => (questionRepaymentConfirmation.Value = val)"
            aria-label="Repayment confirmation"
          />

          <h2 class="font-20 font-weight-bold primary--text mt-10 mb-3">
            Payment details
          </h2>

          <BaseAlert
            color="var(--blue-100)"
            icon="mdi-lock"
            alertText="Your payment is secure."
            alertTextClass="font-18 font-weight-bold primary--text"
            alertBorder
            alertBorderColor="var(--blue-500)"
            alertBorderPosition="left"
            prominent
            prominentColor="var(--blue-500)"
            prominentIconColor="white"
            prominentIconSize="19"
            rounded="0"
            :max-width="$vuetify.breakpoint.lgAndUp ? '440px' : '100%'"
          />

          <BaseLabel text="Account holder name" class="mt-10 mb-2" />
          <BaseInput
            :vid="questionBankAccountName.InterfaceKey"
            v-model="questionBankAccountName.Value"
            dataUiid="AccountHolderName"
            hideDetails="auto"
            :rules="bankAccountNameRule"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Account holder name"
          />

          <BaseLabel text="Sort code" class="mt-10 mb-2" />
          <BaseInput
            :vid="questionBankSortCode.InterfaceKey"
            v-model="questionBankSortCode.Value"
            dataUiid="SortCode"
            hideDetails="auto"
            mask="##-##-##"
            :rules="bankSortCodeRule"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Sort code"
          />

          <BaseLabel text="Account number" class="mt-10 mb-2" />
          <BaseInput
            :vid="questionBankAccountNo.InterfaceKey"
            v-model="questionBankAccountNo.Value"
            dataUiid="AccountNumber"
            hideDetails="auto"
            :rules="bankAccountNoRule"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Account number"
          />

          <BaseLabel text="Preferred DD Date" class="mt-10 mb-2" />
          <BaseSelect
            :vid="questionDirectDebitDate.InterfaceKey"
            v-model="questionDirectDebitDate.Value"
            dataUiid="DirectDebitDate"
            :items="
              setItemDataUiids(
                questionDirectDebitDate.InterfaceKey,
                DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL
              )
            "
            :isEnableSortOrderListItems="false"
            :rules="directDebitDateRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Preferred DD Date"
          />

          <v-alert
            v-if="questionDirectDebitDate.Value"
            color="var(--orange-100)"
            class="mt-4 mb-0 rounded-lg"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--orange-500)">mdi-alert</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Important!</span> It can take up
                to ten working days to set up a Direct Debit which means your
                first payment may not be taken on your preferred date. The date
                of your first payment will be shown in your Welcome Letter. The
                rest of your payments will be taken on the date you choose from
                the options provided.
              </div>
            </div>
          </v-alert>

          <div
            class="d-flex flex-column flex-lg-row align-lg-center mt-10 mb-3"
          >
            <h3 class="font-20 font-weight-bold primary--text">Stay covered</h3>
            <a
              href="#"
              class="d-lg-none font-16 font-weight-bold psdarkgreen--text mt-1"
              @click.prevent="showAutoRenewalInfoMobileDialog"
              >Find out more</a
            >
          </div>
          <BaseRadio
            v-model="questionAutoRenewalConsent.Value"
            data-uiid="AutoRenewalConsent"
            pslStyle="card"
          />
        </template>

        <!-- credit/debit card content -->
        <template v-else>
          <BaseAlert color="var(--blue-100)" class="pa-4 ma-0">
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--blue-500)">mdi-information</v-icon>
              </div>
              <div>
                <p>
                  <span class="font-weight-bold">Important!</span>&nbsp;As
                  you've chosen to pay by annual credit/debit card, you've been
                  opted out of our annual renewal service. If you'd prefer to
                  opt into our auto-renewal service, please change your payment
                  method to <strong>Direct Debit</strong> above.
                </p>
                <p>
                  If you remain opted out of our automatic renewal service,
                  please make sure you have adequate insurance cover in place
                  once your Paymentshield policy comes to an end.
                </p>
                <p>
                  If you own a home with a mortgage, it's often a condition of
                  your mortgage agreement to maintain continuous buildings
                  insurance to protect your home against serious damage such as
                  fire or flood.
                </p>
              </div>
            </div>
          </BaseAlert>

          <template v-if="isAdvisedSeller">
            <BaseLabel
              text="Do you want to use the risk address as the billing address?"
              class="mt-10 mb-2"
              helpText="Please indicate if the customer wishes to use the risk address for credit / debit card billing."
            />
            <BaseToggleButton
              v-model="questionUseBillingAddress.Value"
              :toggleButtonText="{
                yesDataUiid: `${questionUseBillingAddress.InterfaceKey}_Yes`,
                noDataUiid: `${questionUseBillingAddress.InterfaceKey}_No`,
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              :min-width="$vuetify.breakpoint.lgAndUp ? '145' : ''"
            />
          </template>
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3"
          type="submit"
          icon="mdi mdi-lock"
          :loading="isApplying"
          :text="isDirectDebitPayment ? 'PAY NOW' : 'CONTINUE TO PAYMENT'"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :maxWidth="$vuetify.breakpoint.lgAndUp ? '390' : '100%'"
        />
      </form>
    </ValidationObserver>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- missing bank details dialog -->
    <BaseDialog
      ref="dialogDirectDebitMissingBankDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
    >
      <template v-slot:content>
        <DialogContentMissingBankDetails
          @missingBankDetails="handleMissingBankDetails"
          @cancelMissingBankDetails="cancelMissingBankDetails"
          :sortCodeLength="questionBankSortCode.Value.length"
          :accountNumberLength="questionBankAccountNo.Value.length"
        />
      </template>
    </BaseDialog>

    <!-- premium breakdown dialog -->
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <!-- share dialog -->
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="getCover"
          :quoteId="data"
          :showSdn="isSdnCreated"
          :showSuitabilityReport="isSuitabilityReportCreated"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <!-- print dialog -->
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <!-- mobile auto renewal info dialog -->
    <BaseDialog
      ref="autoRenewalInfoMobile"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-2"
      contentClass="mb-0"
    >
      <template v-slot:content>
        <DialogContentAutoRenewalInfoMobile></DialogContentAutoRenewalInfoMobile>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { setItemDataUiids } from "@/constants/component.constants.js";
import {
  PAYMENT_FREQUENCY_ITEMS,
  PAYMENT_FREQUENCY_ITEMS_PSDEFERRED,
  DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
  PAYMENT_METHOD_ITEMS,
  B2B_HJO_PAYMENT_FREQUENCY_ITEMS_ADVISED,
} from "@/constants/payment.constants.js";
import { scrollTop, getErrorMessage, convertToBool } from "@/common/utils";

// LayoutApplicationSummaryCard dependencies
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import {
  ANNUAL,
  MONTHLY,
  MONTHLY_3_MONTHS_DEFERRED,
  DIRECTDEBIT,
} from "@/constants/common.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { getProductGroup } from "@/common/question-set";

import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseDialog from "@/components/BaseComponents/BaseDialog";
import BaseRadio from "@/components/BaseComponents/BaseRadio";

// LayoutApplicationSummaryCard components
import LayoutApplicationSummaryCard from "@/components/LayoutComponents/LayoutApplicationSummaryCard";
import DialogContentMissingBankDetails from "@/views/StageApplication/DialogContentMissingBankDetails";
import DialogContentPremiumBreakdown from "@/components/QuoteResult/DialogContentPremiumBreakdown";
import DialogContentShareQuotes from "@/components/QuoteResult/DialogContentShareQuotes";
import DialogContentPrintQuotes from "@/components/QuoteResult/DialogContentPrintQuotes";
import DialogContentAutoRenewalInfoMobile from "@/views/StageApplication/DialogContentAutoRenewalInfoMobile";

import { EventBus } from "@/common/eventbus";
import {
  isBankDetailsBlank,
  getBankAccountNameRule,
  getBankSortCodeRule,
  getBankAccountNoRule,
} from "@/common/bankDetailsValidation";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton.vue";

export default {
  name: "B2BHomePaymentPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseSelect,
    BaseAlert,
    BaseCheckBox,
    BaseInput,
    BaseDialog,
    BaseRadio,
    LayoutApplicationSummaryCard,
    DialogContentMissingBankDetails,
    DialogContentPremiumBreakdown,
    DialogContentShareQuotes,
    DialogContentPrintQuotes,
    DialogContentAutoRenewalInfoMobile,
    BaseToggleButton,
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      setItemDataUiids,
      PAYMENT_FREQUENCY_ITEMS,
      PAYMENT_FREQUENCY_ITEMS_PSDEFERRED,
      DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
      ANNUAL,
      MONTHLY,
      MONTHLY_3_MONTHS_DEFERRED,
      ROUTE_NAME,

      questionPaymentMethod: {},
      questionPaymentFrequency: {},
      questionRepaymentConfirmation: {},
      questionBankAccountName: {},
      questionBankSortCode: {},
      questionBankAccountNo: {},
      questionDirectDebitDate: {},
      questionAutoRenewalConsent: {},
      questionUseBillingAddress: {},

      // LayoutApplicationSummaryCard dependencies
      quoteDocuments: [],
      files: [
        {
          title: "Suitability report",
          key: "suitability",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Quote and Application Summary",
          key: "qas",
          isVisible: true,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "sdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "factfindsdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
      ],
      documentList: [],
      overlay: false,

      // Payment dependencies
      isApplying: false,
    };
  },
  computed: {
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    ...mapGetters(APPLICATION_MODULE, ["allSections", "selectedQuote"]),
    ...mapGetters(QUESTIONS_MODULE, [
      "getCover",
      "getQuoteRequestId",
      "callChannel",
      "httpConfig",
      "insurerQuotesModel",
      "qsId",
      "branchNumber",
      "isAdvisedSeller",
    ]),
    questionPaymentMethodItems() {
      return this.setItemDataUiids(
        this.questionPaymentMethod.InterfaceKey,
        PAYMENT_METHOD_ITEMS
      );
    },
    questionPaymentFrequencyItems() {
      return this.setItemDataUiids(
        this.questionPaymentFrequency.InterfaceKey,
        this.branchNumber == "ADVISED"
          ? B2B_HJO_PAYMENT_FREQUENCY_ITEMS_ADVISED
          : PAYMENT_FREQUENCY_ITEMS_PSDEFERRED
      );
    },
    productGroup() {
      return getProductGroup(this.qsId);
    },
    isDirectDebitPayment() {
      return this.questionPaymentMethod.Value === DIRECTDEBIT;
    },
    hasMonthlyPayment() {
      return (
        this.questionPaymentFrequency.Value === MONTHLY ||
        this.questionPaymentFrequency.Value === MONTHLY_3_MONTHS_DEFERRED
      );
    },
    isSdnCreated() {
      // Quote docs response has returned and there is an SDN
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some(
          (f) => f.isVisible && (f.key === "sdn" || f.key === "factfindsdn")
        )
      );
    },
    isSuitabilityReportCreated() {
      // Quote docs response has returned and there is an Suitability Report
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "suitability")
      );
    },
    paymentFrequencyForDocs() {
      // Prevent returning an empty string for Payment Frequency if the payment method is card.
      return this.questionPaymentMethod.Value === "CreditCard"
        ? ANNUAL
        : this.questionPaymentFrequency.Value;
    },
    isDirectDebitDetailsValid() {
      return (
        !!this.questionBankSortCode.Value && !!this.questionBankAccountNo.Value
      );
    },
    isDirectDebitDetailsBlank() {
      return isBankDetailsBlank(
        this.questionBankSortCode.Value,
        this.questionBankAccountNo.Value,
        this.questionBankAccountName.Value
      );
    },
    bankAccountNameRule() {
      return getBankAccountNameRule(this.isDirectDebitDetailsBlank);
    },
    bankSortCodeRule() {
      return getBankSortCodeRule(this.isDirectDebitDetailsBlank);
    },
    bankAccountNoRule() {
      return getBankAccountNoRule(this.isDirectDebitDetailsBlank);
    },
    premium() {
      const breakdown = this.buildPremiumBreakdownModel(
        this.selectedQuote,
        "DirectDebit",
        this.questionPaymentFrequency.Value
      );
      return breakdown.premium;
    },
    isDirectDebitDateRequired() {
      return this.questionBankAccountNo.Value ? true : false;
    },
    directDebitDateRules() {
      if (!this.questionBankAccountNo.Value) {
        return {};
      }

      return {
        required: {
          fieldLabel: "Preferred DD Date",
        },
      };
    },
  },
  created() {
    if (this.allSections.length) {
      this.questionPaymentMethod = this.questionMap.PaymentMethod;
      this.questionPaymentFrequency = this.questionMap.PaymentFrequency;
      this.questionRepaymentConfirmation =
        this.questionMap.RepaymentConfirmation;
      this.questionBankAccountName = this.questionMap.BankAccountName;
      this.questionBankSortCode = this.questionMap.BankSortCode;
      this.questionBankAccountNo = this.questionMap.BankAccountNo;
      this.questionDirectDebitDate = this.questionMap.DirectDebitDate;
      this.questionAutoRenewalConsent = this.questionMap.AutoRenewalConsent;

      if (this.isAdvisedSeller) {
        this.questionUseBillingAddress = this.questionMap.UseBillingAddress;

        this.questionUseBillingAddress.Value = convertToBool(
          this.questionUseBillingAddress.Value
        );
      }
      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.questionRepaymentConfirmation.Value = convertToBool(
        this.questionRepaymentConfirmation.Value
      );
      this.questionAutoRenewalConsent.Value = convertToBool(
        this.questionAutoRenewalConsent.Value
      );
    }
  },
  mounted() {
    scrollTop();
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPaymentDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "setRestoringLastStep",
      "getDataToPrintQuote",
      "getDocuments",
      "saveQuoteResults",
      "setCurrentRefObserverForm",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "setCardPaymentStatus",
      "handleApplicationSubmission",
      "setSessionStorageItems",
      "proceedToCardPayment",
      "setPaymentErrorMessage",
      "clearPaymentErrorMessage",
    ]),
    async onSubmit(validate) {
      const isFormValid = await validate();

      if (!isFormValid) {
        EventBus.$emit("validation-error", "Validation error occurred");

        return;
      }

      if (this.isDirectDebitPayment && !this.isDirectDebitDetailsValid) {
        this.$refs.dialogDirectDebitMissingBankDetails.open(
          "Missing bank details"
        );
      } else {
        this.submitForm();
      }
    },
    handleMissingBankDetails() {
      this.$refs.dialogDirectDebitMissingBankDetails.cancel();
      this.submitForm();
    },
    cancelMissingBankDetails() {
      this.$refs.dialogDirectDebitMissingBankDetails.cancel();
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", quoteId);
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    async printQuote(quote) {
      try {
        this.overlay = true;
        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintQuote({
          docs,
          quote,
          paymentFrequency: this.questionMap.PaymentFrequency.Value,
        });
        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    submitForm() {
      this.setCardPaymentStatus("");

      if (this.isDirectDebitPayment) {
        this.submitDirectDebitApplication();
      } else {
        this.submitCardPaymentApplication();
      }
    },
    submitDirectDebitApplication() {
      this.isApplying = true;

      this.handleApplicationSubmission()
        .then(() => {
          this.isApplying = false;
          this.setSessionStorageItems();
          this.clearPaymentErrorMessage();
          this.$router.push({
            name: ROUTE_NAME.HOME.B2B.COMPLETE,
          });
        })
        .catch((error) => {
          this.isApplying = false;
          this.setPaymentErrorMessage(getErrorMessage(error));
          console.log("Apply error:", error);
        });
    },
    submitCardPaymentApplication() {
      this.isApplying = true;

      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.clearPaymentErrorMessage();
          this.proceedToCardPayment();
        })
        .catch((error) => {
          this.isApplying = false;
          this.setPaymentErrorMessage(getErrorMessage(error));
          console.log("Apply error:", error);
        });
    },
    showAutoRenewalInfoMobileDialog() {
      this.$refs.autoRenewalInfoMobile.open("Stay covered");
    },
  },
};
</script>

<style lang="scss" scoped></style>
